import { signalStore, withState, withMethods, patchState, withComputed } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { LookupService } from './lookup.service';
import { LookupViewModel, SubmissionTypeEnum } from '../../models/wholesaler-api-models';

export interface LookupState {
  agencies: LookupViewModel[] | null;
  agents: LookupViewModel[] | null;
  submissionTypes: LookupViewModel[] | null;
  programs: LookupViewModel[] | null;
  submissionStatuses: LookupViewModel[] | null;
  policyStatuses: LookupViewModel[] | null;
  businessTypes: LookupViewModel[] | null;
  programStates: LookupViewModel[] | null;
  states: LookupViewModel[] | null;
  programDeductibles: LookupViewModel[] | null;
  programLimits: LookupViewModel[] | null;
  programClasses: LookupViewModel[] | null;
  documentType: LookupViewModel[] | null;
  systemDocumentType: LookupViewModel[] | null;
  roles: LookupViewModel[] | null;
  agencyRoles: LookupViewModel[] | null;
  wholesalerRoles: LookupViewModel[] | null;
  tenants: LookupViewModel[] | null;
  agencyUserStatus: LookupViewModel[] | null;
  requestTypes: LookupViewModel[] | null;
  cancellationReasons: LookupViewModel[] | null;
  reinstatementReasons: LookupViewModel[] | null;
  isLoading: boolean;
}

export const LookupStore = signalStore(
  { providedIn: 'root' },
  withState<LookupState>({
    agencies: null,
    agents: null,
    submissionTypes: null,
    programs: null,
    submissionStatuses: null,
    policyStatuses: null,
    businessTypes: null,
    programStates: null,
    states: null,
    programDeductibles: null,
    programLimits: null,
    programClasses: null,
    documentType: null,
    systemDocumentType: null,
    roles: null,
    agencyRoles: null,
    wholesalerRoles: null,
    agencyUserStatus: null,
    tenants: null,
    requestTypes: null,
    cancellationReasons: null,
    reinstatementReasons: null,
    isLoading: false,
  }),
  withComputed((state) => ({
    debug: computed(() => ({
      agencies: state.agencies(),
      agents: state.agents(),
      submissionTypes: state.submissionTypes(),
      programs: state.programs(),
      submissionStatuses: state.submissionStatuses(),
      policyStatuses: state.policyStatuses(),
      businessTypes: state.businessTypes(),
      programStates: state.programStates(),
      states: state.states(),
      programDeductibles: state.programDeductibles(),
      programLimits: state.programLimits(),
      programClasses: state.programClasses(),
      documentType: state.documentType(),
      systemDocumentType: state.systemDocumentType(),
      roles: state.roles(),
      agencyRoles: state.agencyRoles(),
      agencyUserStatus: state.agencyUserStatus(),
      wholesalerRoles: state.wholesalerRoles(),
      tenants: state.tenants(),
      requestTypes: state.requestTypes(),
      cancellationReasons: state.cancellationReasons(),
      reinstatementReasons: state.reinstatementReasons(),
      isLoading: state.isLoading(),
    })),
  })),
  withMethods((store, lookupService = inject(LookupService)) => ({
    async loadLookup(name: string, id?: number, programId?: number) {

      patchState(store, { isLoading: true });
      const lookup = await lookupService.getLookup(name, id, programId);
      patchState(store, {
        [name]: lookup,
        isLoading: false,
      });
    },
    async loadAgenciesBySubmission(submissionGuid: string) {
      patchState(store, { isLoading: true });
      const agencies = await lookupService.getAgenciesBySubmission(submissionGuid);
      patchState(store, {
        agencies,
        isLoading: false,
      });
    },
    async loadProgramStates(programCode: string, effectiveDate: string) {
      patchState(store, { isLoading: true });
      const programStates = await lookupService.getProgramStates(programCode, effectiveDate);
      patchState(store, {
        programStates,
        isLoading: false,
      });
    },
    async loadProgramDeductibles(programCode: string, stateCode: string, effectiveDate: string) {
      patchState(store, { isLoading: true });
      const programDeductibles = await lookupService.getProgramDeductibles(programCode, stateCode, effectiveDate);
      patchState(store, {
        programDeductibles,
        isLoading: false,
      });
    },
    async loadProgramLimits(programCode: string, stateCode: string, effectiveDate: string) {
      patchState(store, { isLoading: true });
      const programLimits = await lookupService.getProgramLimits(programCode, stateCode, effectiveDate);
      patchState(store, {
        programLimits,
        isLoading: false,
      });
    },
    async loadProgramClasses(programCode: string, stateCode: string, effectiveDate: string) {
      patchState(store, { isLoading: true });
      const programClasses = await lookupService.getProgramClasses(programCode, stateCode, effectiveDate);
      patchState(store, {
        programClasses,
        isLoading: false,
      });
    },
    clearLookup(name: string) {
      patchState(store, { [name]: null });
    },
    clearLookups() {
      patchState(store, {
        agencies: null,
        agents: null,
        submissionTypes: null,
        programs: null,
        submissionStatuses: null,
        policyStatuses: null,
        businessTypes: null,
        programStates: null,
        states: null,
        programDeductibles: null,
        programLimits: null,
        programClasses: null,
        documentType: null,
        systemDocumentType: null,
        roles: null,
        wholesalerRoles: null,
        tenants: null,
        agencyRoles: null,
        agencyUserStatus: null,
        isLoading: false,
      });
    },
  })),
);
